import type { ExpressPaymentType } from '@stripe/stripe-js';
import analytics from '.';
import {
  CATALOG_ID_BY_SUPPLEMENT_SKU_TYPE,
  CATALOG_ID_CHARGING_BASE,
  CATALOG_ID_MARTINGALE_BAND,
  CATALOG_ID_SERIES_3_PLUS_BAND,
  CATALOG_VARIANT_BY_COLOR,
  getCatalogProductId,
} from './catalogIds';
import { SubscriptionOption } from '../../components/SubscriptionOptionDetails';
import type { PauseStatus } from '../pauseStatus';
import {
  Address,
  BillingInfo,
  Cart,
  CartItem,
  CartPricing,
  ColorOption,
  HardwareDiscountPreview,
  IProduct,
  ISubscriptionProduct,
  IVariant,
  PaymentContext,
  ProductCategory,
  SizeOption,
  SupplementCheckoutFlow,
  CollarProductId,
  series1CollarId,
  series2CollarId,
  series3CollarId,
  series3PlusCollarId,
  miniCollarId,
  ultraCollarId,
  productIdIsCollar,
  isModuleSubscriptionProduct,
} from '../../types';
import {
  AxonUserData,
  cartItemToAxonItems,
  maybeAddAxonSetupFeeItem,
  productEventPropsToAxonItem,
} from '../../types/Axon';
import * as gqlTypes from '../../types/gql-op-types';
import { SubscriptionCategory } from '../../views/ManageSubscription/Collar/util/types';
import { CancellationDeterrents } from '../../views/ManageSubscription/Supplements/util/types';
import { BandVariantGroup } from '../../views/ProductDetails/components/BandVariantOptions/BandVariantOptions';
import { FiCountry } from '../../contexts/LocaleContext';
import { MeasurementUnit, WeightUnit } from '../size';
import { isBundleCartItem, isUpgradeCartItem, skusForCartItem } from '../cart';
import { getProductsAndVariantsBySku, variantDescriptionForAnalytics } from '../product';
import store from '../reduxStore';
import { centsToDollarsNumber, expectUnreachable } from '../util';
import { isMobileDevice } from '../util/isMobileDevice';
import { typedWindow } from '../Window';
import {
  getCartInformation,
  getAnalyticsProductPriceInCents,
  getAnalyticsSubscriptionValueInCents,
} from './analyticsUtils';
import * as postie from './postie';
import { CustomerIO } from './customerIO';
import {
  hardwareFirstEventProperties,
  hardwareFirstProductProperties,
  hardwareFirstSurfaceProperties,
} from './hardwareFirstEvents';

// Re-exported so callers keep importing ids from `events`, which is where every other
// analytics symbol lives.
export {
  CATALOG_ID_BY_SUPPLEMENT_SKU_TYPE,
  CATALOG_ID_CHARGING_BASE,
  CATALOG_ID_MARTINGALE_BAND,
  CATALOG_ID_SERIES_3_PLUS_BAND,
  getCatalogProductId,
};

export type PaymentType = ExpressPaymentType | 'credit_card' | 'paypal' | 'ach' | 'klarna';

export function getAxonUserData(): AxonUserData | undefined {
  const session = store.getState().session;
  const traits = analytics.userTraits();
  const userId = session?.userId;
  const email = session?.email;
  const phone = traits.phone;

  if (!userId && !email && !phone) {
    return undefined;
  }

  return {
    ...(userId && { user_id: userId }),
    ...(email && { email }),
    ...(phone && { phone }),
  };
}

export interface EventDetails {
  eventName: string;
  eventProps?: any;
}

interface PageLoad {
  kind: 'PageLoad';
}

interface VariantChanged {
  kind: 'VariantChanged';
  previousVariant: IVariant;
}

interface ProductChanged {
  kind: 'ProductChanged';
}

export type ProductViewReason = PageLoad | VariantChanged | ProductChanged;

type EComEventName =
  | 'Product Viewed'
  | 'Product Added'
  | 'Cart Viewed'
  | 'Checkout Started'
  | 'Checkout Step Completed'
  | 'Shipping Completed'
  | 'Payment Info Entered'
  | 'Product Removed'
  | 'Order Completed'
  | 'Subscription Selected'
  | 'Subscription Plans Displayed';

export const ECOM_EVENT_PRODUCT_VIEWED: EComEventName = 'Product Viewed';
export const ECOM_EVENT_PRODUCT_ADDED: EComEventName = 'Product Added';
export const ECOM_EVENT_CART_VIEWED: EComEventName = 'Cart Viewed';
export const ECOM_EVENT_CHECKOUT_STARTED: EComEventName = 'Checkout Started';
export const ECOM_EVENT_CHECKOUT_STEP_COMPLETED: EComEventName = 'Checkout Step Completed';
export const ECOM_EVENT_SHIPPING_COMPLETED: EComEventName = 'Shipping Completed';
export const ECOM_EVENT_PAYMENT_INFO_ENTERED: EComEventName = 'Payment Info Entered';
export const ECOM_EVENT_PRODUCT_REMOVED: EComEventName = 'Product Removed';
export const ECOM_EVENT_ORDER_COMPLETED: EComEventName = 'Order Completed';
export const ECOM_EVENT_SUBSCRIPTION_SELECTED: EComEventName = 'Subscription Selected';
export const ECOM_EVENT_SUBSCRIPTION_PLANS_DISPLAYED: EComEventName = 'Subscription Plans Displayed';

export type ProductFamilyId = 'F1' | 'F2' | 'F3' | 'F3B' | 'M1' | 'U1';
export type ProductFamilyName = 'Series 1' | 'Series 2' | 'Series 3' | 'Series 3+' | 'Mini' | 'Ultra';

function getProductFamilyId(collarProductId: CollarProductId) {
  switch (collarProductId) {
    case series1CollarId:
      return 'F1';
    case series2CollarId:
      return 'F2';
    case series3CollarId:
      return 'F3';
    case series3PlusCollarId:
      return 'F3B';
    case miniCollarId:
      return 'M1';
    case ultraCollarId:
      return 'U1';
    default:
      expectUnreachable(collarProductId);
      return undefined;
  }
}

function getProductFamilyName(collarProductId: CollarProductId) {
  switch (collarProductId) {
    case series1CollarId:
      return 'Series 1';
    case series2CollarId:
      return 'Series 2';
    case series3CollarId:
      return 'Series 3';
    case series3PlusCollarId:
      return 'Series 3+';
    case miniCollarId:
      return 'Mini';
    case ultraCollarId:
      return 'Ultra';
    default:
      expectUnreachable(collarProductId);
      return undefined;
  }
}

export interface SegmentProduct {
  id: string;
  product_id: string;
  sku: string;
  name: string;
  variant?: string;
  price: number;
  quantity: number;
  currency: string;
  isUpgrade?: boolean;
  subscriptionPrice?: number;
  /**
   * Analytics price for external marketing pixels (e.g., Meta, Northbeam).
   * When present, external analytics destinations should use this instead of subscriptionPrice.
   * Only set for subscription products that have analyticsPricingInCents configured on the backend.
   */
  analyticsSubscriptionValue?: number;
  subscriptionRenewalMonths?: number;
  freeTrial?: boolean;
  productFamilyId?: ProductFamilyId;
  productFamilyName?: ProductFamilyName;
  catalogProductId?: string;
  isGift?: boolean;
  /** hardware-first only: the list and server-repriced figures for the row */
  list_price?: number;
  discounted_price?: number;
}

export interface SegmentCartEventProperties {
  products: SegmentProduct[];
  total?: number;
  value?: number;
  currency: string;
}

export interface SegmentPurchaseEventProperties {
  orderId: string;
  products: SegmentProduct[];
  total: number;
  shipping?: number;
  tax?: number;
  code?: string;
  isGWP?: boolean;
  currency: string;
  /**
   * Money the customer was actually charged, for commission pixels that pay out against it.
   * `total` is the marketing figure: pre-discount and inflated by free-trial subscription value.
   */
  chargedSubtotal?: number;
  chargedTotal?: number;
}

function skuToSegmentProduct(sku: string, currency: string): SegmentProduct | undefined {
  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);
  const productAndVariant = productsAndVariantsBySku.get(sku);
  if (!productAndVariant) {
    return undefined;
  }

  const { product, variant } = productAndVariant;

  // Determine product family based on product ID
  let productFamilyId: ProductFamilyId | undefined;
  let productFamilyName: ProductFamilyName | undefined;

  if (productIdIsCollar(product.id)) {
    productFamilyId = getProductFamilyId(product.id);
    productFamilyName = getProductFamilyName(product.id);
  }

  const subscriptionRenewalMonths = isModuleSubscriptionProduct(product) ? product.renewalMonths : undefined;
  const freeTrial = isModuleSubscriptionProduct(product) ? product.freeTrial : undefined;

  // For subscription products with analytics pricing, compute the LTV for marketing pixels.
  // This represents expected lifetime value rather than literal billing price.
  const analyticsPricingInCents = isModuleSubscriptionProduct(product)
    ? getAnalyticsSubscriptionValueInCents(product, currency)
    : undefined;

  const analyticsSubscriptionValue =
    analyticsPricingInCents != null ? centsToDollarsNumber(analyticsPricingInCents) : undefined;

  return {
    id: sku,
    product_id: sku,
    sku,
    name: product.name,
    variant: variant ? variantDescriptionForAnalytics(variant) : undefined,
    price: centsToDollarsNumber(getAnalyticsProductPriceInCents(product, currency)),
    quantity: 1,
    currency,
    productFamilyId,
    productFamilyName,
    subscriptionRenewalMonths,
    freeTrial,
    analyticsSubscriptionValue,
  };
}

// One cart item may map to multiple products for tracking purposes-
// specifically, bundles might include addon products (e.g. a collar kit with a second band)
export function cartItemToSegmentProducts(
  cartItem: CartItem,
  currency: string,
  hardwareDiscount?: HardwareDiscountPreview,
) {
  const isUpgrade = isUpgradeCartItem(cartItem);

  if (isBundleCartItem(cartItem)) {
    const products: SegmentProduct[] = [];

    const subscriptionProduct = skuToSegmentProduct(cartItem.subscriptionLineItem.sku, currency);
    if (subscriptionProduct) {
      products.push({ ...subscriptionProduct, isUpgrade });
    }

    const mainProduct = skuToSegmentProduct(cartItem.lineItem.sku, currency);
    if (mainProduct) {
      products.push({
        ...mainProduct,
        isUpgrade,
        analyticsSubscriptionValue: subscriptionProduct?.analyticsSubscriptionValue,
        subscriptionPrice: subscriptionProduct?.price,
        freeTrial: subscriptionProduct?.freeTrial,
      });
    }

    if (cartItem.addonLineItems) {
      for (const addon of cartItem.addonLineItems) {
        const addonProduct = skuToSegmentProduct(addon.sku, currency);
        if (addonProduct) {
          products.push({ ...addonProduct, isUpgrade });
        }
      }
    }

    return products;
  } else {
    const segmentProduct = skuToSegmentProduct(cartItem.lineItem.sku, currency);
    if (!segmentProduct) {
      return [];
    }

    return [{ ...segmentProduct, isUpgrade, ...hardwareFirstProductProperties(hardwareDiscount) }];
  }
}

function cartItemsToSegmentProducts(
  cartItems: { [id: string]: CartItem },
  currency: string,
  hardwareDiscounts?: CartPricing['hardwareDiscounts'],
) {
  let segmentProducts: SegmentProduct[] = [];

  for (const cartItem of Object.values(cartItems)) {
    const cartItemProducts = cartItemToSegmentProducts(cartItem, currency, hardwareDiscounts?.[cartItem.cartItemId]);
    segmentProducts = segmentProducts.concat(cartItemProducts);
  }

  return segmentProducts;
}

export type HardwareFirstAddSource = 'hardware_first_plp' | 'cart_upsell' | 'abandoned_cart_link';

/**
 * The standard product payload for a hardware-first surface: the server-quoted hardware price
 * stands in for the catalog price once the preview lands, with the list and repriced figures
 * alongside it. Undefined for a sku the catalog does not know.
 */
function hardwareFirstProductPayload(sku: string, currency: string, discount: HardwareDiscountPreview | undefined) {
  const product = skuToSegmentProduct(sku, currency);
  if (!product) {
    return undefined;
  }

  return {
    ...product,
    price: discount ? centsToDollarsNumber(discount.discountedPriceInCents) : product.price,
    ...hardwareFirstProductProperties(discount),
    ...hardwareFirstSurfaceProperties(),
  };
}

/**
 * Hardware-first product view: the pick-your-fi shop has no product page of its own, so the
 * selected tracker fires the standard product viewed event with the price the card shows.
 */
export function hardwareFirstProductViewed({
  sku,
  currency,
  discount,
}: {
  sku: string;
  currency: string;
  discount: HardwareDiscountPreview;
}) {
  const product = hardwareFirstProductPayload(sku, currency, discount);
  if (!product) {
    return;
  }

  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, { ...product, source: 'hardware_first_plp' });
}

/**
 * Hardware-first add to cart: the standard product added event carrying the server-quoted
 * hardware price and the surface the add came from. The catalog price stands in until the
 * preview lands.
 */
export function hardwareFirstProductAdded({
  sku,
  currency,
  source,
  discount,
}: {
  sku: string;
  currency: string;
  source: HardwareFirstAddSource;
  discount?: HardwareDiscountPreview;
}) {
  const product = hardwareFirstProductPayload(sku, currency, discount);
  if (!product) {
    return;
  }

  analytics.track(ECOM_EVENT_PRODUCT_ADDED, { ...product, source });
}

export const cartUpsell = {
  viewed(props: { products: string[]; savingInCents: number; trackerCount: number }) {
    analytics.track('Cart > Upsell > Viewed', {
      products: props.products,
      saving: centsToDollarsNumber(props.savingInCents),
      tracker_count: props.trackerCount,
    });
  },
};

export function subscriptionSelected(product: ISubscriptionProduct, currency: string) {
  const fiEventProps = {
    plan: product.id,
    sku: product.sku,
    months: product.renewalMonths,
    price: getAnalyticsProductPriceInCents(product, currency),
  };
  analytics.track(ECOM_EVENT_SUBSCRIPTION_SELECTED, fiEventProps);
}

export function subscriptionPlansDisplayed(products: ISubscriptionProduct[], productName: string, currency: string) {
  analytics.track(ECOM_EVENT_SUBSCRIPTION_PLANS_DISPLAYED, {
    productName,
    plans: products.map((p) => ({
      sku: p.sku,
      months: p.renewalMonths,
      price: getAnalyticsProductPriceInCents(p, currency),
      billingCadence: p.billingCadence,
    })),
  });
}

export interface ProductEventProps {
  priceInCents: number;
  productName: string;
  quantity?: number;
  sku: string;
  isUpgrade?: boolean;
  productFamilyId?: ProductFamilyId;
  productFamilyName?: ProductFamilyName;
  isGift?: boolean;
  currency: string;
  /** Analytics price of the bundled subscription for marketing pixels (in cents). */
  analyticsSubscriptionValueInCents?: number;
  /**
   * Catalog id to report for this product. Set wherever the specific variant is known —
   * a colourway, a species, an accessory. Falls back to the product family when absent.
   */
  catalogProductId?: string;
}

function ecommerceProductEventProperties({
  priceInCents,
  productName,
  quantity = 1,
  sku,
  isUpgrade,
  productFamilyId,
  productFamilyName,
  catalogProductId,
  isGift,
  currency,
  analyticsSubscriptionValueInCents,
}: ProductEventProps): SegmentProduct {
  return {
    id: sku,
    name: productName,
    price: centsToDollarsNumber(priceInCents),
    product_id: sku,
    quantity,
    sku,
    currency,
    isUpgrade,
    productFamilyId,
    productFamilyName,
    catalogProductId,
    isGift,
    analyticsSubscriptionValue: analyticsSubscriptionValueInCents
      ? centsToDollarsNumber(analyticsSubscriptionValueInCents)
      : undefined,
  };
}

/**
 * The supplements page shows one flavour at a time, so a flavour selection is a product view.
 * Price comes from the product config rather than the caller — the page renders price in a
 * child component, and a stale figure here would put the wrong price in live ads.
 */
export function supplementViewed({ skuType, sku, currency }: { skuType: string; sku: string; currency: string }) {
  const catalogProductId = CATALOG_ID_BY_SUPPLEMENT_SKU_TYPE[skuType];
  const product = getProductsAndVariantsBySku(store.getState().config.products).get(sku)?.product;
  if (!catalogProductId || !product) {
    return;
  }

  analytics.track(
    ECOM_EVENT_PRODUCT_VIEWED,
    ecommerceProductEventProperties({
      priceInCents: product.priceInCents,
      productName: product.name,
      sku,
      currency,
      catalogProductId,
    }),
  );
}

export function chargingBaseViewed(props: ProductEventProps) {
  // Fi event
  analytics.track('Base page', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function chargingBaseAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Base page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function miniKitViewed(props: ProductEventProps) {
  // Fi event
  analytics.track('Mini kit page', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function miniKitAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Mini kit page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function miniProductViewed(props: ProductEventProps & { speciesName?: 'Dog' | 'Cat' }) {
  // Fi event
  analytics.track('Mini page', {});

  const { speciesName, ...productProps } = props;
  const productFamilyName = getProductFamilyName(miniCollarId);

  // Segment standardized ecommerce event
  analytics.track(
    ECOM_EVENT_PRODUCT_VIEWED,
    ecommerceProductEventProperties({
      ...productProps,
      productFamilyId: getProductFamilyId(miniCollarId),
      productFamilyName,
      catalogProductId: productFamilyName ? getCatalogProductId(productFamilyName, speciesName) : undefined,
    }),
  );

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function miniAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Mini page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(
    ECOM_EVENT_PRODUCT_ADDED,
    ecommerceProductEventProperties({
      ...props,
      productFamilyId: getProductFamilyId(miniCollarId),
      productFamilyName: getProductFamilyName(miniCollarId),
    }),
  );

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function freeTrialLearnMoreClickedFromSubscriptionOption() {
  analytics.track('Free trial > Learn more clicked > Subscription option', {});
}

export function freeTrialLearnMoreClickedFromCart() {
  analytics.track('Free trial > Learn more clicked > Cart', {});
}

export function startFreeTrialClickedFromLearnMoreModal() {
  analytics.track('Free trial > Start free trial clicked > Learn more modal', {});
}

export function ultraPDPShoppingForPickerShown(props: { eligiblePetCount: number }) {
  analytics.track('Ultra PDP > Shopping for picker shown', props);
}

export function ultraPDPShoppingForSelected(props: { choice: 'existing' | 'new'; eligiblePetCount: number }) {
  analytics.track('Ultra PDP > Shopping for selected', props);
}

export function comparePageViewed() {
  analytics.track('Compare page', {});
}

export function comparePageShopAllClicked() {
  analytics.track('Compare page > Shop all', {});
}

export function comparePageBuyItClicked(productName: string) {
  analytics.track('Compare page > Buy it', { product: productName });
}

export function comparePageGPSViewed() {
  analytics.track('Compare page > GPS', {});
}

export function comparePageHealthViewed() {
  analytics.track('Compare page > Health', {});
}

export function comparePageVetsViewed() {
  analytics.track('Compare page > Vets', {});
}

export function comparePageScrolledToBottom() {
  analytics.track('Compare page > Scrolled to bottom', {});
}

export function comparePageFreeTrialStartFreeTrial() {
  analytics.track('Compare page > Free Trial > Start Free Trial', {});
}

export function navFreeTrialClicked() {
  analytics.track('Nav > Free Trial > Try Fi for free', {});
}

export function trialLandingPageViewed() {
  analytics.track('Trial LP > Viewed', {});
}

export function trialLandingDeviceCtaClicked(device: 'series3plus' | 'mini') {
  analytics.track('Trial LP > Device CTA clicked', { device });
}

export function trialLandingFeatureTabSelected(tab: string) {
  analytics.track('Trial LP > Feature tab selected', { tab });
}

export function trialLandingChooseDeviceCtaClicked(placement: 'hero' | 'floating') {
  analytics.track('Trial LP > Choose device CTA clicked', { placement });
}

export function trialLandingHelpMeChooseClicked() {
  analytics.track('Trial LP > Help me choose clicked', {});
}

export function productSwitcherClicked(pageName: string) {
  analytics.track('Product switcher > Clicked', { pageName });
}

export function miniSpeciesToggle(species: string) {
  analytics.track('Mini page > Species toggle', { species });
}

export function miniPickYourMembershipClicked() {
  analytics.track('Mini page > Pick your membership', {});
}

/**
 * The rescue variant de-emphasizes the plan picker, which starves `Mini page > Pick your
 * membership` on that path - this is its replacement funnel step, fired once when the offer box
 * first enters the viewport. The picker event stays for generic traffic.
 */
export function miniOfferShown() {
  analytics.track('Mini page > Offer shown', {});
}

export function miniViewedSpecifications() {
  analytics.track('Mini page > Viewed specifications', {});
}

export function miniFullSpecsExpand() {
  analytics.track('Mini page > Full specs expand', {});
}

export function miniFullSpecsCollapse() {
  analytics.track('Mini page > Full specs collapse', {});
}

export function miniViewedFeatures() {
  analytics.track('Mini page > Viewed features', {});
}

export function miniViewedMiniPlus() {
  analytics.track('Mini page > Viewed Mini+', {});
}

export function miniViewedFAQ() {
  analytics.track('Mini page > Viewed FAQ', {});
}

export function miniCarouselScrolled(position: number, direction: 'next' | 'prev') {
  analytics.track('Mini page > Carousel scrolled', {
    name: position.toString(),
    selectedValue: direction,
  });
}

export function nanoViewed(props: ProductEventProps) {
  // Fi event
  analytics.track('Nano page', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function nanoAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Nano page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function endlinksViewed(props: ProductEventProps) {
  // Fi event
  analytics.track('Endlinks page', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function endlinksAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Endlinks page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

interface ProductWithVariantEventProps extends ProductEventProps {
  color: ColorOption;
  size: SizeOption;
}

interface CollarKitVariantEventProps extends ProductWithVariantEventProps {
  subscriptionPrice?: number;
  analyticsSubscriptionValueInCents?: number;
  subscriptionRenewalMonths?: number;
}

function ecommerceProductWithVariantEventProperties({
  color,
  size,
  ...productEventProps
}: ProductWithVariantEventProps): SegmentProduct {
  const { productFamilyName } = productEventProps;
  const variantName = CATALOG_VARIANT_BY_COLOR[color];

  return {
    ...ecommerceProductEventProperties({
      ...productEventProps,
      catalogProductId: productFamilyName ? getCatalogProductId(productFamilyName, variantName) : undefined,
    }),
    variant: `${size} ${color}`,
  };
}

function collarKitProductWithVariantEventProperties({
  subscriptionPrice,
  analyticsSubscriptionValueInCents,
  subscriptionRenewalMonths,
  ...productWithVariantEventProps
}: CollarKitVariantEventProps): SegmentProduct {
  return {
    ...ecommerceProductWithVariantEventProperties(productWithVariantEventProps),
    subscriptionPrice: subscriptionPrice ? centsToDollarsNumber(subscriptionPrice) : undefined,
    analyticsSubscriptionValue: analyticsSubscriptionValueInCents
      ? centsToDollarsNumber(analyticsSubscriptionValueInCents)
      : undefined,
    subscriptionRenewalMonths,
  };
}

/**
 * Tracks event when a user lands on the product page for a spare band
 */
export function bandViewed(props?: ProductEventProps) {
  analytics.track('Band page', {});

  if (props) {
    analytics.track(
      ECOM_EVENT_PRODUCT_VIEWED,
      ecommerceProductEventProperties({ ...props, catalogProductId: CATALOG_ID_SERIES_3_PLUS_BAND }),
    );
  }
}

export function bandColorSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Band page > Color switch', { new_color: props.color });
}

export function bandSizeSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Band page > Size switch', { new_size: props.size });
}

export function bandVariantSwitch(props: ProductWithVariantEventProps) {
  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function bandAddedToBag(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Band page > Add to bag', {
    color: props.color,
    size: props.size,
  });

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

/**
 * Tracks event when a user lands on the product page for an S2 martingale band
 */
export function martingaleBandViewed(props?: ProductEventProps) {
  analytics.track('Martingale band page', {});

  if (props) {
    analytics.track(
      ECOM_EVENT_PRODUCT_VIEWED,
      ecommerceProductEventProperties({ ...props, catalogProductId: CATALOG_ID_MARTINGALE_BAND }),
    );
  }
}

export function martingaleBandColorSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Martingale band page > Color switch', { new_color: props.color });
}

export function martingaleBandSizeSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Martingale band page > Size switch', { new_size: props.size });
}

export function martingaleBandVariantSwitch(props: ProductWithVariantEventProps) {
  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function martingaleBandAddedToBag(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Martingale band page > Add to bag', {
    color: props.color,
    size: props.size,
  });

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

/**
 * Tracks event when a user lands on the S3 PDP
 */
export function series3CollarViewed() {
  // Fi event
  analytics.track('Collar page', {});
}

export function series3CollarColorSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Color switch', { new_color: props.color });
}

export function series3CollarSizeSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Size switch', { new_size: props.size });
}

export function series3CollarMeasurementUnitSwitch(country: FiCountry, unit: MeasurementUnit) {
  // Fi event
  analytics.track('Collar page > Measurement unit switch', { country, unit });
}

/**
 * Tracks the lb/kg toggle on the S3 PDP size selector, which drives the weight range shown
 * on each size card.
 */
export function series3CollarWeightUnitSwitch(country: FiCountry, unit: WeightUnit) {
  // Fi event
  analytics.track('Collar page > Weight unit switch', { country, unit });
}

/**
 * Tracks event when a user changes the selected variant on the S3 PDP
 */
export function series3CollarVariantSwitch(props: ProductWithVariantEventProps) {
  // Segment standardized ecommerce event
  analytics.track(
    ECOM_EVENT_PRODUCT_VIEWED,
    ecommerceProductWithVariantEventProperties({
      ...props,
      productFamilyId: getProductFamilyId(series3PlusCollarId),
      productFamilyName: getProductFamilyName(series3PlusCollarId),
    }),
  );

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function series3CollarAddedToBag(props: CollarKitVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Add to bag', {
    color: props.color,
    size: props.size,
  });

  // Segment standardized ecommerce event
  analytics.track(
    ECOM_EVENT_PRODUCT_ADDED,
    collarKitProductWithVariantEventProperties({
      ...props,
      productFamilyId: getProductFamilyId(series3PlusCollarId),
      productFamilyName: getProductFamilyName(series3PlusCollarId),
    }),
  );

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function series3SubscriptionAddedToBag(props: SubscriptionEventProps) {
  // Fi event
  analytics.track('GPS page > Add to bag', {
    plan: props.productId,
  });

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function series3CollarActionButtonClick(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Action button click', ecommerceProductWithVariantEventProperties(props));
}

export function series3CollarWhatSizeIsRight() {
  // Fi event
  analytics.track('Collar page > What size is right', {});
}

export function series3CollarSizingGuideBreedSelectorShown() {
  // Fi event
  analytics.track('Collar page > Sizing guide breed selector shown', {});
}

export function series3CollarSizingGuideBreedSelected(breed: string) {
  // Fi event
  analytics.track('Collar page > Sizing guide breed selected', { breed });
}

export function series3CollarSizingGuideWeightUpdate(previousWeight: number, newWeight: number) {
  // Fi event
  analytics.track('Collar page > Sizing guide weight update', { previousWeight, newWeight });
}

export function series3CollarSizingGuideRecommendationSelected(
  breed: string | undefined,
  weight: number,
  recommendedSize: SizeOption,
) {
  // Fi event
  analytics.track('Collar page > Sizing guide recommendation selected', { breed, weight, recommendedSize });
}

export function series3CollarEditionSwitch(bandVariantGroup: BandVariantGroup) {
  // Fi event
  analytics.track('Collar page > Edition switch', { bandVariantGroup });
}

export function series3CollarFiMakers() {
  // Fi event
  analytics.track('Collar page > Fi Makers', {});
}

export function series3CollarCarouselInteraction(index: number) {
  // Fi event
  analytics.track('Collar page > Carousel interaction', { index });
}

export function FAQExpand(page: string, index: number) {
  // Fi event
  analytics.track(`${page} page > FAQ expand`, { index });
}

export function series3CollarFAQChatButtonClick() {
  // Fi event
  analytics.track('Collar page > FAQ > Chat with Fi', {});
}

export function series3CollarMoreReviews() {
  // Fi event
  analytics.track('Collar page > More reviews', {});
}

export function series3CollarViewedAIBehaviorMonitoring() {
  // Fi event
  analytics.track('Collar page > Viewed AI behavior monitoring', {});
}

export function series3CollarViewedSpecifications() {
  // Fi event
  analytics.track('Collar page > Viewed specifications', {});
}

export function series3CollarFullSpecsExpand() {
  analytics.track('Collar page > Full specs expand', {});
}

export function series3CollarFullSpecsCollapse() {
  analytics.track('Collar page > Full specs collapse', {});
}

export function series3CollarViewedFeatures() {
  // Fi event
  analytics.track('Collar page > Viewed features', {});
}

export function series3CollarViewedFAQ() {
  // Fi event
  analytics.track('Collar page > Viewed FAQ', {});
}

export function series3CollarViewedReviews() {
  // Fi event
  analytics.track('Collar page > Viewed reviews', {});
}

export function series3CollarCarouselScrolled(position: number, direction: 'next' | 'prev') {
  analytics.track('Shop > Collar page > Carousel scrolled', {
    name: position.toString(),
    selectedValue: direction,
  });
}

/**
 * Tracks event when a user lands on the Series 2 collar PDP
 */
export function series2CollarViewed() {
  // Fi event
  analytics.track('Collar page', {});
}

export function series2CollarColorSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Color switch', { new_color: props.color });
}

export function series2CollarSizeSwitch(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Size switch', { new_size: props.size });
}

export function series2CollarVariantSwitch(props: ProductWithVariantEventProps) {
  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function series2CollarAddedToBag(props: ProductWithVariantEventProps) {
  // Fi event
  analytics.track('Collar page > Add to bag', {
    color: props.color,
    size: props.size,
  });

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductWithVariantEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function series2SubscriptionAddedToBag(props: SubscriptionEventProps) {
  // Fi event
  analytics.track('GPS page > Add to bag', {
    plan: props.productId,
  });

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function giftCardViewed(props: ProductEventProps) {
  // Fi event
  analytics.track('Gift card page', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function giftCardSwitch(props: ProductEventProps) {
  // Fi event
  analytics.track('Gift card page > Change package', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_VIEWED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'view_item', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export function giftCardAddedToBag(props: ProductEventProps) {
  // Fi event
  analytics.track('Gift card page > Add to bag', {});

  // Segment standardized ecommerce event
  analytics.track(ECOM_EVENT_PRODUCT_ADDED, ecommerceProductEventProperties(props));

  if (typedWindow.axon) {
    typedWindow.axon('track', 'add_to_cart', {
      currency: props.currency || 'USD',
      items: [productEventPropsToAxonItem(props)],
      user_data: getAxonUserData(),
    });
  }
}

export interface SubscriptionEventProps extends ProductEventProps {
  productId: string;
}

export function productCategoryNameForEvent(productCategory: ProductCategory): string {
  switch (productCategory) {
    case ProductCategory.BAND:
      return 'band';
    case ProductCategory.CHARGING_BASE:
      return 'base';
    case ProductCategory.COLLAR_KIT:
      return 'collar';
    case ProductCategory.ENDLINK:
      return 'endlinks';
    case ProductCategory.GIFT_CARD:
      return 'gift card';
    case ProductCategory.MICROCHIP:
      return 'nano';
    case ProductCategory.MODULE:
      return 'module';
    case ProductCategory.SUBSCRIPTION:
      return 'subscription';
    case ProductCategory.S2_UPGRADE:
      return 'S2 upgrade';
    case ProductCategory.SUPPLEMENT:
      return 'supplement';
    case ProductCategory.THIRD_PARTY_MEMBERSHIP:
      return 'third party membership';
    case ProductCategory.EARLY_RENEWAL:
      return 'early renewal';
    case ProductCategory.MINI:
      return 'mini';
    case ProductCategory.MINI_ATTACHMENT:
      return 'mini attachment';
    case ProductCategory.ULTRA:
      return 'ultra';
    default:
      expectUnreachable(productCategory);
      throw new Error(`Unknown product category: ${productCategory}`);
  }
}

export function cartItemRemoved({ cartItem, currency }: { cartItem: CartItem; currency: string }) {
  const skus = skusForCartItem(cartItem);
  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);

  for (const sku of skus) {
    const productAndVariant = productsAndVariantsBySku.get(sku);
    if (!productAndVariant) {
      continue;
    }

    const { product } = productAndVariant;

    // Fi event
    analytics.track(`Cart page > Remove ${productCategoryNameForEvent(product.category)}`, {});

    // ECom event
    const segmentProduct = skuToSegmentProduct(sku, currency);
    analytics.track(ECOM_EVENT_PRODUCT_REMOVED, segmentProduct);
  }

  // Axon event
  if (typedWindow.axon) {
    const axonItems = cartItemToAxonItems(cartItem, currency);
    typedWindow.axon('track', 'remove_from_cart', {
      currency,
      items: axonItems,
      user_data: getAxonUserData(),
    });
  }
}

export function orderCompleted(
  cart: Cart,
  cartPricing: CartPricing,
  orderId: string,
  shippingAddress: Address | undefined,
  billingInfo: BillingInfo | undefined,
  additionalProperties: {
    isReturningCustomer: boolean;
    isGWP?: boolean;
    currency?: string;
    paymentType?: PaymentType;
    integrations?: {
      [integrationName: string]: boolean;
    };
  },
) {
  const cartItemsArray = Object.values(cart.cartItems);
  const currency = additionalProperties.currency || cartPricing.currency || 'USD';
  const products = cartItemsToSegmentProducts(cart.cartItems, currency, cartPricing.hardwareDiscounts);
  const couponCode = cart.couponCode;
  const cartInformation = getCartInformation(cartItemsArray, cartPricing);
  const total = (cartInformation && cartInformation.revenueIncludingFreeTrials) || undefined;
  const shipping = cartInformation?.shipping;
  const tax = cartInformation?.tax;
  // `subtotalInCents` is the list subtotal with the coupon still to come off as its own line, so
  // netting the discount out here is what leaves the figure a commission is owed on.
  const chargedSubtotal = centsToDollarsNumber(
    Math.max(cartPricing.subtotalInCents - cartPricing.appliedDiscountInCents, 0),
  );
  const chargedTotal = centsToDollarsNumber(cartPricing.totalInCents);

  // https://segment.com/docs/destinations/google-analytics/#required-steps
  // N.B. Order completed event requires an "orderId"
  analytics.track(
    ECOM_EVENT_ORDER_COMPLETED,
    {
      orderId,
      products,
      total,
      shipping,
      tax,
      currency,
      chargedSubtotal,
      chargedTotal,
      code: couponCode,
      new_customer: !additionalProperties.isReturningCustomer,
      isGWP: additionalProperties.isGWP,
      payment_type: additionalProperties.paymentType ?? 'credit_card',
      is_gift: !!cart.isGift,
      ...hardwareFirstEventProperties(cartPricing, couponCode),
    },
    { integrations: additionalProperties?.integrations },
  );

  if (shippingAddress && billingInfo) {
    postie.trackPurchase(cartItemsArray, cartPricing, orderId, shippingAddress, billingInfo);
  }

  if (typedWindow.axon) {
    const axonItems = maybeAddAxonSetupFeeItem(
      cartPricing,
      cartItemsArray.flatMap((item) => cartItemToAxonItems(item, currency)),
    );
    typedWindow.axon('track', 'purchase', {
      currency,
      items: axonItems,
      value: total ?? centsToDollarsNumber(cartPricing.totalInCents),
      transaction_id: orderId,
      shipping,
      tax,
      user_data: getAxonUserData(),
    });
  }
}

export const subscriptionPage = {
  viewed() {
    analytics.track('Subscription Page', {});
  },
};

export const subscriptionUpsell = {
  viewed(props: SubscriptionEventProps) {
    analytics.track('Subscription upsell modal', {
      plan: props.productId,
    });
  },

  acceptedUpsell(props: SubscriptionEventProps) {
    analytics.track('Subscription upsell modal > Accepted upsell', {
      plan: props.productId,
    });
  },

  ignoredUpsell(props: SubscriptionEventProps) {
    analytics.track('Subscription upsell modal > Ignored upsell', {
      plan: props.productId,
    });
  },
};

/**
 * Dimensions that separate a genuine coupon failure from the empty-cart lookup that can never
 * succeed. Without them the event cannot distinguish an entitled adopter losing their discount from
 * a bare product-page mount probing recurly.js for an internal alias it will never know about, and
 * the two have to be told apart by stitching sessions together after the fact.
 */
export interface CouponErrorContext {
  /** Cart was empty, so nothing was validated and a `not-found` says nothing about the code. */
  cartEmpty: boolean;
  /** A signed GWA/GwP entitlement was present, i.e. the shopper was genuinely owed the discount. */
  hadEntitlement: boolean;
  /** Which surface attempted the apply, so the auto-apply sites stay distinguishable. */
  surface: string;
}

/**
 * A cart-abandonment email link opened on an empty bag. Separate event names (not an outcome
 * property) so each count is queryable on its own; the bag URL in the event context carries the
 * email's UTMs.
 */
export const abandonedCartLink = {
  restored(itemCount: number) {
    analytics.track('Cart > Abandoned Link > Restored', { item_count: itemCount });
  },
  sentToShop() {
    analytics.track('Cart > Abandoned Link > Sent To Shop', {});
  },
};

export const cartPage = {
  viewed(cartItems: { [id: string]: CartItem }, currency: string, cartPricing?: CartPricing, couponCode?: string) {
    // Fi event
    analytics.track('Cart page', {});

    // ECom event
    const products = cartItemsToSegmentProducts(cartItems, currency, cartPricing?.hardwareDiscounts);
    analytics.track(ECOM_EVENT_CART_VIEWED, {
      products,
      currency,
      ...hardwareFirstEventProperties(cartPricing, couponCode),
    });

    // Axon event - only fire if cartPricing is available
    if (typedWindow.axon && cartPricing) {
      const cartItemsArray = Object.values(cartItems);
      const axonItems = maybeAddAxonSetupFeeItem(
        cartPricing,
        cartItemsArray.flatMap((item) => cartItemToAxonItems(item, currency)),
      );
      const cartInformation = getCartInformation(cartItemsArray, cartPricing);
      typedWindow.axon('track', 'view_cart', {
        currency,
        items: axonItems,
        value: cartInformation?.revenueIncludingFreeTrials ?? centsToDollarsNumber(cartPricing.totalInCents),
        user_data: getAxonUserData(),
      });
    }
  },

  checkout(cartItems: { [id: string]: CartItem }, cartPricing: CartPricing, couponCode?: string, isGift?: boolean) {
    // Fi event
    analytics.track('Cart page > Checkout', {});

    // Ecom event
    const cartItemsArray = Object.values(cartItems);
    const currency = cartPricing.currency || 'USD';
    const products = cartItemsToSegmentProducts(cartItems, currency, cartPricing.hardwareDiscounts);
    const cartInformation = getCartInformation(cartItemsArray, cartPricing);
    const value = cartInformation?.revenueIncludingFreeTrials ?? centsToDollarsNumber(cartPricing.totalInCents);
    analytics.track(ECOM_EVENT_CHECKOUT_STARTED, {
      products,
      value,
      currency,
      is_gift: !!isGift,
      ...hardwareFirstEventProperties(cartPricing, couponCode),
    });

    CustomerIO.trackStartedCheckout(products);

    if (typedWindow.axon) {
      const axonItems = maybeAddAxonSetupFeeItem(
        cartPricing,
        cartItemsArray.flatMap((item) => cartItemToAxonItems(item, currency)),
      );
      typedWindow.axon('track', 'begin_checkout', {
        currency,
        items: axonItems,
        value,
        user_data: getAxonUserData(),
      });
    }
  },

  couponApplied(code: string) {
    analytics.track('Cart page > Coupon applied', { code });
  },

  // auto-applied coupons never reach couponApplied, so without this a shopper who was given the
  // discount is indistinguishable from one who was not.
  couponAutoApplied(code: string, source?: string) {
    analytics.track('Cart page > Coupon auto applied', { code, source });
  },

  // `message` carries the error *type* rather than a localized string — see `codeResultFailureType`.
  couponError(code: string, message: string, context: CouponErrorContext) {
    analytics.track('Cart page > Coupon error', {
      code,
      message,
      ...context,
    });
  },

  referralCodeApplied(code: string) {
    analytics.track('Cart page > Referral code applied', { code });
  },

  giftCardApplied(code: string) {
    analytics.track('Cart page > Gift card applied', { code });
  },

  giftCardError(code: string, message: string) {
    analytics.track('Cart page > Gift card error', {
      code,
      message,
    });
  },

  couponRemoved(code: string) {
    analytics.track('Cart page > Coupon removed', { code });
  },

  nonSafariApplePayClick() {
    analytics.track('Cart page > Non Safari Apple pay click', {});
  },

  nonSafariApplePayClose() {
    analytics.track('Cart page > Non Safari Apple pay closed', {});
  },

  applePayBegin() {
    analytics.track('Cart page > Apple pay begin', {});
  },

  applePaySuccess() {
    analytics.track('Cart page > Apple pay success', {});
  },

  applePayError(message: string) {
    analytics.track('Cart page > Apple pay error', { message });
  },

  applePayCancel() {
    analytics.track('Cart page > Apple pay cancel', {});
  },

  paypalUnavailable(products: IProduct[]) {
    analytics.track('Cart page > PayPal unavailable for products', {
      products,
    });
  },

  paypalButtonBegin() {
    analytics.track('Cart page > PayPal button begin', {});
  },

  paypalButtonSuccess() {
    analytics.track('Cart page > PayPal button successfully got a token', {});
  },

  paypalButtonError(message: string) {
    analytics.track('Cart page > PayPal button error', { message });
  },

  paypalButtonCancel() {
    analytics.track('Cart page > PayPal button cancel', {});
  },

  tariffFeeModalOpen() {
    analytics.track('Cart page > Tariff fee modal open', {});
  },

  tariffFeeModalClose() {
    analytics.track('Cart page > Tariff fee modal close', {});
  },
};

function checkoutStepCompleted(cartItems: CartItem[], cartPricing: CartPricing) {
  const cartInformation = getCartInformation(cartItems, cartPricing);
  const currency = cartPricing.currency || 'USD';
  const segmentProducts = cartItems.flatMap((item) => cartItemToSegmentProducts(item, currency));
  analytics.track(ECOM_EVENT_CHECKOUT_STEP_COMPLETED, {
    products: segmentProducts,
    ...(cartInformation && {
      value: cartInformation.revenueIncludingFreeTrials,
      currency,
    }),
  });
}

export const shipping = {
  viewed() {
    analytics.track('Shipping page', {});
  },

  continue(cartItems: { [id: string]: CartItem }, currency: string) {
    analytics.track('Shipping page > Continue', {});

    const products = cartItemsToSegmentProducts(cartItems, currency);
    analytics.track(ECOM_EVENT_SHIPPING_COMPLETED, { products, currency });
    CustomerIO.trackShippingPageContinue(products);
  },

  continueError(message?: string) {
    analytics.track('Shipping page > Continue Error', {
      message,
    });
  },

  verifyAddressViewed() {
    analytics.track('Shipping page > Verify address', {});
  },

  verifyAddressConfirmed() {
    analytics.track('Shipping page > Verify address confirmed', {});
  },

  verifyAddressEdit() {
    analytics.track('Shipping page > Verify address re-edit', {});
  },
};

export const shippingOptions = {
  viewed() {
    analytics.track('Shipping options', {});
  },

  continue() {
    analytics.track('Shipping options > Continue', {});
  },
};

export const payment = {
  viewed() {
    analytics.track('Payment page', {});
  },

  continue(
    cartItems: { [id: string]: CartItem },
    currency: string,
    options?: { cartPricing?: CartPricing; paymentType?: PaymentType },
  ) {
    const { cartPricing, paymentType } = options ?? {};
    // Fi event - include paymentType (apple_pay, google_pay, ach, etc.) so the
    // analytics warehouse can attribute wallet purchases.
    analytics.track('Payment page > Payment Success', paymentType ? { paymentType } : {});

    // Ecom event
    const products = cartItemsToSegmentProducts(cartItems, currency);
    analytics.track(ECOM_EVENT_PAYMENT_INFO_ENTERED, {
      products,
    });

    // Axon event - only fire if cartPricing is available
    if (typedWindow.axon && cartPricing) {
      const cartItemsArray = Object.values(cartItems);
      const axonItems = maybeAddAxonSetupFeeItem(
        cartPricing,
        cartItemsArray.flatMap((item) => cartItemToAxonItems(item, currency)),
      );
      const cartInformation = getCartInformation(cartItemsArray, cartPricing);
      typedWindow.axon('track', 'add_payment_info', {
        currency,
        items: axonItems,
        payment_type: paymentType ? 'wallet' : 'credit_card',
        value: cartInformation?.revenueIncludingFreeTrials ?? centsToDollarsNumber(cartPricing.totalInCents),
        user_data: getAxonUserData(),
      });
    }
  },

  continueError(message?: string) {
    analytics.track('Payment page > Payment Error', { message });
  },

  threeDSecurePrompt() {
    analytics.track('Payment page > Prompting ThreeDSecure authentication', {});
  },

  threeDSecureSuccess() {
    analytics.track('Payment page > ThreeDSecure success', {});
  },

  threeDSecureError(message: string) {
    analytics.track('Payment page > ThreeDSecure error', { message });
  },

  paypalUnavailable(products: IProduct[]) {
    analytics.track('Payment page > PayPal unavailable for products', {
      products,
    });
  },

  paypalTokenSuccess() {
    analytics.track('Payment page > PayPal success', {});
  },

  paypalTokenError(message: string) {
    analytics.track('Payment page > PayPal error', { message });
  },

  paypalUnlink() {
    analytics.track('Payment page > PayPal unlink', {});
  },

  paypalButtonBegin() {
    analytics.track('Payment page > PayPal button begin', {});
  },

  paypalButtonSuccess() {
    analytics.track('Payment page > PayPal button successfully got a token', {});
  },

  paypalButtonError(message: string) {
    analytics.track('Payment page > PayPal button error', { message });
  },

  paypalButtonCancel() {
    analytics.track('Payment page > PayPal button cancel', {});
  },
};

export const confirmation = {
  viewed() {
    analytics.track('Confirmation Page', {});
  },

  finalConfirm(cartItems: CartItem[], cartPricing: CartPricing) {
    analytics.track('Confirmation Page > Final Confirm', {});
    checkoutStepCompleted(cartItems, cartPricing);
  },

  placeOrder(
    upgradeNewSku: SubscriptionOption,
    context: PaymentContext,
    petId?: string | null,
    upgradeOldSku?: string | null,
  ) {
    analytics.track(`${context} > Place Order`, { petId, upgradeOldSku, upgradeNewSku });
  },

  error(message?: string) {
    analytics.track('Confirmation Page > Error', { message });
  },

  threeDSecurePrompt() {
    analytics.track('Confirmation Page > Prompting ThreeDSecure authentication', {});
  },

  threeDSecureSuccess() {
    analytics.track('Confirmation Page > ThreeDSecure success', {});
  },

  threeDSecureError(message: string) {
    analytics.track('Confirmation Page > ThreeDSecure error', { message });
  },
};

export const checkoutFunnel = {
  emailCaptured(email: string) {
    analytics.track('Checkout Funnel > Email Captured', { email });
  },
  infoSubmitted(checkoutFlow: 'stripe') {
    analytics.track('Checkout Funnel > Info Submitted', { checkout_flow: checkoutFlow });
  },
  placeOrderClicked(checkoutFlow: 'stripe') {
    analytics.track('Checkout Funnel > Place Order Clicked', { checkout_flow: checkoutFlow });
  },
  purchaseSucceeded(checkoutFlow: 'stripe') {
    analytics.track('Checkout Funnel > Purchase Succeeded', { checkout_flow: checkoutFlow });
  },
  /**
   * The order summary is a collapsed drawer on mobile only; on desktop it's always visible, so this
   * fires solely for shoppers who chose to expand it.
   */
  orderSummaryOpened(checkoutFlow: 'stripe') {
    analytics.track('Checkout Funnel > Order Summary Opened', { checkout_flow: checkoutFlow });
  },
};

const SUBSCRIBE_AND_SAVE = 'Supplements Subscribe & Save';
export const supplements = {
  viewed() {
    analytics.track('Supplements page', {});
  },
  getItClicked(flow: SupplementCheckoutFlow) {
    analytics.track(`Supplements ${flow} > Get it`, {});
  },
  purchaseSucceeded(flow: SupplementCheckoutFlow, fiveDollarFirstBagOfferLive: boolean) {
    analytics.track(
      `Supplements ${flow}${
        fiveDollarFirstBagOfferLive ? ' $5 First Bag' : ''
      } > Get it > Purchase successfully went through`,
      {},
    );
  },
  noThanksClicked() {
    analytics.track('Supplements page > No, thanks', {});
  },
  openPlanSelection(flow: SupplementCheckoutFlow) {
    analytics.track(`Supplements ${flow} > Plan Selection`, {
      context: getSupplementsManagementEventContext(),
    });
  },
  closePlanSelection(flow: SupplementCheckoutFlow) {
    analytics.track(`Supplements ${flow} > Plan Selection > Close`, {
      context: getSupplementsManagementEventContext(),
    });
  },
  upsellV2: {
    addSupplementClicked() {
      analytics.track('Supplements Upsell V2 > Add Supplement Clicked', {});
    },
    confirm() {
      analytics.track('Supplements Upsell V2 > Confirm', {});
    },
    notNow() {
      analytics.track('Supplements Upsell V2 > Not Now', {});
    },
    skuClicked(sku: string) {
      analytics.track('Supplements Upsell V2 > SKU Clicked', { sku });
    },
    whatsInsideClicked(sku: string) {
      analytics.track('Supplements Upsell V2 > Whats Inside Clicked', { sku });
    },
    buyOnceOptionClicked() {
      analytics.track('Supplements Upsell V2 > Buy Once Option Clicked', {});
    },
    autoshipOptionClicked() {
      analytics.track('Supplements Upsell V2 > Autoship Option Clicked', {});
    },
    tryItWeeklyOptionClicked() {
      analytics.track('Supplements Upsell V2 > Try It (weekly pricing)', {});
    },
    pageLoad() {
      analytics.track('Supplements Upsell V2 > Page Load', {});
    },
    experiment: {
      tryItWeeklyOptionClicked() {
        analytics.track('Supplements Upsell Experiment > $5 First Bag > Try It', {});
      },
      confirm() {
        analytics.track('Supplements Upsell Experiment > $5 First Bag > Confirm', {});
      },
      noThanks() {
        analytics.track('Supplements Upsell Experiment > $5 First Bag > No thanks', {});
      },
    },
  },
  // We're removing subscribeAndSave from the in-app experience, but will keep 'ecomm'
  // in the event name so the dashboards reliant on analytic events don't break.
  subscribeAndSave: {
    buyOneBagSelected() {
      analytics.track(`${SUBSCRIBE_AND_SAVE} ecomm > Buy one bag option selected`, {});
    },
    subscribeAndSaveSelected() {
      analytics.track(`${SUBSCRIBE_AND_SAVE} ecomm > Subscribe and save option selected`, {});
    },
    purchaseButtonClicked(buttonCopy: string) {
      analytics.track(`${SUBSCRIBE_AND_SAVE} ecomm > ${buttonCopy} button clicked`, {});
    },
    purchaseSucceeded() {
      analytics.track(`${SUBSCRIBE_AND_SAVE} ecomm > Purchase successfully went through`, {});
    },
    noThanksClicked() {
      analytics.track(`${SUBSCRIBE_AND_SAVE} ecomm > No, thanks`, {});
    },
    purchaseConfirmationModalContinue(buttonCopy: string) {
      analytics.track(
        `${SUBSCRIBE_AND_SAVE} ecomm > ${buttonCopy} button clicked > Confirmation modal viewed > Confirm`,
        {},
      );
    },
    purchaseConfirmationModalGoBack(buttonCopy: string) {
      analytics.track(
        `${SUBSCRIBE_AND_SAVE} ecomm > ${buttonCopy} button clicked > Confirmation modal viewed > No, go back`,
        {},
      );
    },
  },
};

export const thankYou = {
  viewed() {
    analytics.track('Thank you page', {});
  },

  setPasswordSuccess() {
    analytics.track('Thank you page > Set password success', {});
  },

  setPasswordFailure() {
    analytics.track('Thank you page > Set password failure', {});
  },

  downloadLinkDetails(): EventDetails {
    return { eventName: 'Thank you page > Download clicked' };
  },

  surveySubmit({
    userId,
    selectedValue,
    email,
    orderId,
    couponCode,
  }: {
    userId?: string;
    selectedValue: string;
    email?: string;
    orderId?: string;
    /** Lets us check attribution-code answers (podcast, creator) against the code actually redeemed. */
    couponCode?: string;
  }): void {
    analytics.track('Thank you page > Survey Submitted', {
      userId,
      selectedValue,
      email,
      orderId,
      couponCode,
    });
  },
};

export const referrals = {
  codeCopied() {
    analytics.track('Order Confirmation > Share Fi > Code Copied', {});
  },

  nativeShareClicked() {
    analytics.track('Order Confirmation > Share Fi > Code Share Clicked', {});
  },

  nativeShareCanceled() {
    analytics.track('Order Confirmation > Share Fi > Code Share Canceled', {});
  },

  nativeShareSuccess() {
    analytics.track('Order Confirmation > Share Fi > Code Share Success', {});
  },

  shareClicked(nativeShare: boolean) {
    analytics.track('Referrals Page > Share Clicked', { nativeShare });
  },
};

export const referralRedirect = {
  loaded(code: string) {
    analytics.track('Referral Redirect > Loaded', { code });
  },
};

export const accessories = {
  fiMakerClick: (name: string): EventDetails => ({
    eventName: `Accessories page > Fi Maker clicked`,
    eventProps: {
      name,
    },
  }),
};

interface InAppCancellationProps {
  petId?: string;
  cancellationReason?: gqlTypes.zendeskCancellationReason;
  replacementReason?: string;
  otherReasonText?: string;
  oldSku?: string;
  newSku?: string;
  subscriptionCategory?: SubscriptionCategory;
  /** Chosen pause length, 1-3. */
  pauseMonths?: number;
  /**
   * Plan length. Required to segment pause uptake, because `subscriptionCategory` collapses every
   * 3/6/12-month plan into a single PREPAID bucket.
   */
  renewalMonths?: number;
  /** Pause state at the moment a resume was confirmed — the read on accidental-charge rate. */
  pauseStatusAtResume?: PauseStatus['kind'];
}

function getInAppCancellationEventProperties(props: InAppCancellationProps) {
  return {
    petId: props.petId,
    cancellationReason: props.cancellationReason,
    cancellationText: props.otherReasonText,
    replacementReason: props.replacementReason,
    oldSku: props.oldSku,
    newSku: props.newSku,
    subscriptionCategory: props.subscriptionCategory,
    pauseMonths: props.pauseMonths,
    renewalMonths: props.renewalMonths,
    pauseStatusAtResume: props.pauseStatusAtResume,
  };
}

// Returns an object containing Segment context fields.
function getInAppCancellationEventContext() {
  // Params search string looks like "?platform=android&app_version=3.5.41-internal"
  const paramsString = window.location.search;
  const searchParams = new URLSearchParams(paramsString);
  return {
    os: { name: searchParams.get('platform') },
    app: { version: searchParams.get('app_version') },
  };
}

export const inAppCancellation = {
  manageMembershipClicked(props: InAppCancellationProps) {
    analytics.track('Membership > Manage membership clicked', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  manageMembershipBack(props: InAppCancellationProps) {
    analytics.track('Membership > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  manageMembershipClose(props: InAppCancellationProps) {
    analytics.track('Membership > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  updatePaymentMethodClicked(props: InAppCancellationProps) {
    analytics.track('Membership > Update payment method clicked', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  updatePaymentBack(props: InAppCancellationProps) {
    analytics.track('Membership > Update payment > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  updatePaymentClose(props: InAppCancellationProps) {
    analytics.track('Membership > Update payment > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewMembership(props: InAppCancellationProps) {
    analytics.track('Membership > Renew', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewMembershipContinue(props: InAppCancellationProps) {
    analytics.track('Renew Membership > Continue', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewMembershipBack(props: InAppCancellationProps) {
    analytics.track('Renew Membership > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewMembershipClose(props: InAppCancellationProps) {
    analytics.track('Renew Membership > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track('Renew > Done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  resumeConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track('Resume > Done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  requestToCancelMembershipClicked(props: InAppCancellationProps) {
    analytics.track(
      'Manage Membership > Request to cancel membership clicked',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  cancelFromReasonPage(props: InAppCancellationProps) {
    analytics.track('Cancellation Reasons > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  backFromReasonPage(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Cancel Reason > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  closeFromReasonPage(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Cancel Reason > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeToS3Clicked(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Upgrade to S3 clicked', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  changeMembershipSaveClicked(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Change membership save clicked', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  changeMembershipBack(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Change membership > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  changeMembershipClose(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Change membership > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancellationTicketConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track(
      'Manage Membership > Cancellation ticket confirmation done clicked',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  cancellationReasonSelectedAndClicked(props: InAppCancellationProps) {
    analytics.track(
      'Manage Membership > Cancellation reason selected and clicked',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  cancellationReasonOtherSelectedAndClicked(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Other Reason > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancellationReasonOtherBack(props: InAppCancellationProps) {
    analytics.track('Manage Membership > Other Reason > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  requestToCancelMembershipSubmitted(props: InAppCancellationProps) {
    analytics.track(
      'Manage Membership > Request to cancel membership submitted',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  upgradeS2Cancel(props: InAppCancellationProps) {
    analytics.track('Upgrade your series 2 collar > Cancel clicked', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeS2Back(props: InAppCancellationProps) {
    analytics.track('Upgrade your series 2 collar > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeS2Close(props: InAppCancellationProps) {
    analytics.track('Upgrade your series 2 collar > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeS2TicketSubmitted(props: InAppCancellationProps) {
    analytics.track(
      'Upgrade your series 2 collar > Cancel ticket created',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  upgradeS2CheckoutFlow(props: InAppCancellationProps, pageName: string) {
    analytics.track(`S2 Upgrade > ${pageName}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeS2CheckoutV2Flow(props: InAppCancellationProps, pageName: string) {
    analytics.track(`Upgrade V2 > ${pageName}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  verizonCheckoutFlow(props: InAppCancellationProps, pageName: string) {
    analytics.track(`Verizon > ${pageName}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  chatWithCx(props: InAppCancellationProps) {
    analytics.track('Contact CX > Chat', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  callCx(props: InAppCancellationProps) {
    analytics.track('Contact CX > Call', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelFromContactPage(props: InAppCancellationProps) {
    analytics.track('Contact CX > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  contactCxBack(props: InAppCancellationProps) {
    analytics.track('Contact CX > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  contactCxClose(props: InAppCancellationProps) {
    analytics.track('Contact CX > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelFromFreeMonthPage(props: InAppCancellationProps) {
    analytics.track('Free month > cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimFreeMonth(props: InAppCancellationProps) {
    analytics.track('Free month > claim', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimFreeMonthDone(props: InAppCancellationProps) {
    analytics.track('Free month > claim > done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimFreeMonthBack(props: InAppCancellationProps) {
    analytics.track('Free month > claim > back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimFreeMonthClose(props: InAppCancellationProps) {
    analytics.track('Free month > claim > close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelFromPauseMembershipPage(props: InAppCancellationProps) {
    analytics.track('Pause membership > cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimPauseMembership(props: InAppCancellationProps) {
    analytics.track('Pause membership > claim', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  /** `claimPauseMembership` fires before the mutation resolves, so the outcome needs its own event. */
  pauseMembershipSucceeded(props: InAppCancellationProps) {
    analytics.track('Pause membership > succeeded', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  pauseMembershipFailed(props: InAppCancellationProps) {
    analytics.track('Pause membership > failed', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  resumeMembershipSucceeded(props: InAppCancellationProps) {
    analytics.track('Resume membership > succeeded', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  resumeMembershipFailed(props: InAppCancellationProps) {
    analytics.track('Resume membership > failed', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimPauseMembershipDone(props: InAppCancellationProps) {
    analytics.track('Pause membership > claim > done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimPauseMembershipBack(props: InAppCancellationProps) {
    analytics.track('Pause membership > claim > back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  claimPauseMembershipClose(props: InAppCancellationProps) {
    analytics.track('Pause membership > claim > close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  resumeMembership(props: InAppCancellationProps) {
    analytics.track('Resume membership > confirm', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  multidogDiscountBack(props: InAppCancellationProps) {
    analytics.track('Multidog Discount > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  multidogDiscountClose(props: InAppCancellationProps) {
    analytics.track('Multidog Discount > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  multidogDiscountCancelClicked(props: InAppCancellationProps) {
    analytics.track('Multidog Discount > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  multidogDiscountApplied(props: InAppCancellationProps) {
    analytics.track('Multidog Discount > Applied', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  multidogDiscountConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track('Multidog Discount > Applied > Done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayKeepMembership(props: InAppCancellationProps) {
    analytics.track('Dog Does Not Run Away > Keep Membership', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayContinue(props: InAppCancellationProps) {
    analytics.track('Dog Does Not Run Away > Continue', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayCancel(props: InAppCancellationProps) {
    analytics.track('Dog Does Not Run Away > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayBack(props: InAppCancellationProps, page: number) {
    analytics.track(`Dog Does Not Run Away > P${page} > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayClose(props: InAppCancellationProps, page: number) {
    analytics.track(`Dog Does Not Run Away > P${page} > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  dogDoesNotRunAwayNext(props: InAppCancellationProps, page: number) {
    analytics.track(`Dog Does Not Run Away > P${page} > Next`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  replaceCollarBack(props: InAppCancellationProps) {
    analytics.track(`Physically Damaged > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  replaceCollarClose(props: InAppCancellationProps) {
    analytics.track(`Physically Damaged > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  replacementTicketSubmitted(props: InAppCancellationProps) {
    analytics.track('Physically Damaged > Submit Replacement Request', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  replacementTicketConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track(
      'Physically Damaged > Submit Replacement Request > Done',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  series3UpgradeSplashContinueClicked(props: InAppCancellationProps) {
    analytics.track('Membership > Series 3 Upgrade Splash > Continue', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  series2UpgradePageUpgradeClicked(props: InAppCancellationProps) {
    analytics.track('Upgrade your Series 2 collar Page > Upgrade Now', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  upgradeToS3Checkout(props: InAppCancellationProps) {
    analytics.track(
      'Upgrade Now > S3 Plan Selected > Continue > Checkout',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  upgradeToS3CheckoutComplete(props: InAppCancellationProps) {
    analytics.track(
      'Upgrade Now > S3 Plan Selected > Continue > Checkout > Thank You Page',
      getInAppCancellationEventProperties(props),
      {
        context: getInAppCancellationEventContext(),
      },
    );
  },
  downgradeS2Cancel(props: InAppCancellationProps) {
    analytics.track('Downgrade S2 > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS2Back(props: InAppCancellationProps) {
    analytics.track('Downgrade S2 > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS2Close(props: InAppCancellationProps) {
    analytics.track('Downgrade S2 > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS3SelectPlan(props: InAppCancellationProps) {
    analytics.track('Downgrade S3 > Select Plan', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS3Cancel(props: InAppCancellationProps) {
    analytics.track('Downgrade S3 > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS3Back(props: InAppCancellationProps) {
    analytics.track('Downgrade S3 > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS3Close(props: InAppCancellationProps) {
    analytics.track('Downgrade S3 > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  downgradeS3ConfirmationDone(props: InAppCancellationProps) {
    analytics.track('Downgrade S3 > Confirmation > Done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveKeepMembership(props: InAppCancellationProps) {
    analytics.track('Too Expensive > Keep Membership', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveContinue(props: InAppCancellationProps) {
    analytics.track('Too Expensive > Continue', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveCancel(props: InAppCancellationProps) {
    analytics.track('Too Expensive > Cancel', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveBack(props: InAppCancellationProps, page: number) {
    analytics.track(`Too Expensive > P${page} > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveClose(props: InAppCancellationProps, page: number) {
    analytics.track(`Too Expensive > P${page} > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tooExpensiveNext(props: InAppCancellationProps, page: number) {
    analytics.track(`Too Expensive > P${page} > Next`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  pageLoad(props: InAppCancellationProps, pageName: string) {
    analytics.track(`Page Load: ${pageName}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  subscriptionSplashButtonsLoaded(props: InAppCancellationProps, buttons: string) {
    analytics.track(`Subscription Splash Buttons Loaded: ${buttons}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tellUsMoreBack(props: InAppCancellationProps) {
    analytics.track(`Tell Us More > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tellUsMoreClose(props: InAppCancellationProps) {
    analytics.track(`Tell Us More > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  tellUsMoreContinue(props: InAppCancellationProps) {
    analytics.track(`Tell Us More > Continue`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  meetMakersBack(props: InAppCancellationProps) {
    analytics.track(`Meet Makers > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  meetMakersClose(props: InAppCancellationProps) {
    analytics.track(`Meet Makers > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  meetMakersContinue(props: InAppCancellationProps) {
    analytics.track(`Meet Makers > Continue`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  specialOffersBack(props: InAppCancellationProps) {
    analytics.track(`Special Offers > Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  specialOffersClose(props: InAppCancellationProps) {
    analytics.track(`Special Offers > Close`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  specialOffersContinue(props: InAppCancellationProps) {
    analytics.track(`Special Offers > Continue`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelFromSpecialOffersPage(props: InAppCancellationProps) {
    analytics.track('Special Offers > Continue with cancellation', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  specialOffersClaim(props: InAppCancellationProps, offer: string) {
    analytics.track(`Special Offers > Claim special offer: ${offer}`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  specialOffersConfirmationDoneClicked(props: InAppCancellationProps) {
    analytics.track('Special Offers Confirmation > Done', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  meetMakersExplore(props: InAppCancellationProps) {
    analytics.track(`Meet Makers > Explore`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  keepMembershipConfirmationBack(props: InAppCancellationProps) {
    analytics.track('Keep Membership Confirmation > Back', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  keepMembershipConfirmationClose(props: InAppCancellationProps) {
    analytics.track('Keep Membership Confirmation > Close', getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelConfirmationModalContinue(props: InAppCancellationProps, pageName: string) {
    analytics.track(`${pageName} > Cancel Confirmation > Continue`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  cancelConfirmationModalGoBack(props: InAppCancellationProps, pageName: string) {
    analytics.track(`${pageName} > Cancel Confirmation > Go Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewConfirmationModalContinue(props: InAppCancellationProps, pageName: string) {
    analytics.track(`${pageName} > Renew Confirmation > Continue`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  renewConfirmationModalGoBack(props: InAppCancellationProps, pageName: string) {
    analytics.track(`${pageName} > Renew Confirmation > Go Back`, getInAppCancellationEventProperties(props), {
      context: getInAppCancellationEventContext(),
    });
  },
  troubleshootButtonClicked(props: TroubleshootProps) {
    analytics.track(`Cancel > Need collar help? Troubleshoot`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
};

interface SupplementsManagementProps {
  subscriptionId?: string;
  cancellationReason?: gqlTypes.supplementCancellationReason;
  cancellationText?: string;
  oldSku?: string;
  newSku?: string;
}

function getSupplementsManagementEventProperties(props: SupplementsManagementProps) {
  return {
    subscriptionId: props.subscriptionId,
    cancellationReason: props.cancellationReason,
    cancellationText: props.cancellationText,
    oldSku: props.oldSku,
    newSku: props.newSku,
  };
}

// Returns an object containing Segment context fields.
function getSupplementsManagementEventContext() {
  // Params search string looks like "?platform=android&app_version=3.5.41-internal"
  const paramsString = window.location.search;
  const searchParams = new URLSearchParams(paramsString);
  return {
    os: { name: searchParams.get('platform') },
    app: { version: searchParams.get('app_version') },
  };
}

export const supplementsManagement = {
  goBackFromPage(props: SupplementsManagementProps, pageName: string) {
    analytics.track(`Supplements > ${pageName} > Back`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  closePage(props: SupplementsManagementProps, pageName: string) {
    analytics.track(`Supplements > ${pageName} > Close`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  pageLoad(props: SupplementsManagementProps, pageName: string) {
    analytics.track(`Supplements > Page Load: ${pageName}`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  cancelSubscription(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Update Cadence > Cancel tapped`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  cancelConfirmationModalContinue(props: SupplementsManagementProps, pageName: string) {
    analytics.track(
      `Supplements > ${pageName} > Cancel Confirmation > Continue`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  cancelConfirmationModalGoBack(props: SupplementsManagementProps, pageName: string) {
    analytics.track(
      `Supplements > ${pageName} > Cancel Confirmation > Go Back`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  purchaseConfirmationModalContinue(props: SupplementsManagementProps, pageName: string) {
    analytics.track(
      `Supplements > ${pageName} > Purchase Confirmation > Purchase`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  purchaseConfirmationModalGoBack(props: SupplementsManagementProps, pageName: string) {
    analytics.track(
      `Supplements > ${pageName} > Purchase Confirmation > Go Back`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  addSupplementsTapped(props: SupplementsManagementProps) {
    analytics.track(`Supplements > Add Supplements tapped`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  manageSubscription(props: SupplementsManagementProps) {
    analytics.track(`Supplements > Manage tapped`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  shipmentFrequencyTapped(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Shipment frequency tapped`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  shipmentFrequencyUpdated(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Subscription frequency updated`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  updateShipmentDateTapped(props: SupplementsManagementProps, referrer?: string) {
    analytics.track(
      `Supplements > Manage > ${referrer ? `${referrer} > ` : ''}Update shipment date tapped`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  setShipDate(props: SupplementsManagementProps) {
    analytics.track(`Supplements > Manage > Shipment date updated`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  sendNowTapped(props: SupplementsManagementProps) {
    analytics.track(`Supplements > Manage > Send now tapped`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  defaultSendNowAccepted(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Send now > Default accepted`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  skipShipmentTapped(props: SupplementsManagementProps) {
    analytics.track(`Supplements > Manage > Skip shipment tapped`, getSupplementsManagementEventProperties(props), {
      context: getSupplementsManagementEventContext(),
    });
  },
  modalCloseEvent(props: SupplementsManagementProps, modalName: string) {
    analytics.track(
      `Supplements > Manage > ${modalName} > Close modal`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  defaultSkipAccepted(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Skip shipment > Default accepted`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  unableToSkipViewed(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Skip shipment > Unable to skip viewed`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  deterrentTapped(props: SupplementsManagementProps, deterrent: CancellationDeterrents) {
    analytics.track(
      `Supplements > Cancel > ${deterrent} Deterrent Tapped`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  deterrentAccepted(props: SupplementsManagementProps, deterrent: CancellationDeterrents) {
    analytics.track(
      `Supplements > Cancel > ${deterrent} Deterrent Tapped > Confirm`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },

  deterrentDeclined(props: SupplementsManagementProps, deterrent: CancellationDeterrents) {
    analytics.track(
      `Supplements > Cancel > ${deterrent} Deterrent Tapped > Continue with cancellation`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  deterrentConfirmationGoBack(props: SupplementsManagementProps, deterrent: CancellationDeterrents) {
    analytics.track(
      `Supplements > Cancel > ${deterrent} Deterrent Tapped > Go back`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  updateShipmentAddressTapped(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Update shipment address tapped`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  updateShipmentAddress(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Update shipment address > Update address`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  updateJustThisSubscriptionsAddress(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Update shipment address > Just this subscription`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
  updateAllSubscriptionAddresses(props: SupplementsManagementProps) {
    analytics.track(
      `Supplements > Manage > Update shipment address > All subscriptions`,
      getSupplementsManagementEventProperties(props),
      {
        context: getSupplementsManagementEventContext(),
      },
    );
  },
};

interface MonthlyUpgradeProps {
  newSku?: string;
}

function getMonthlyUpgradeEventProperties(props: MonthlyUpgradeProps) {
  return {
    newSku: props.newSku,
  };
}

export const monthlyUpgrade = {
  viewed() {
    analytics.track('Monthly Upgrade Webview Viewed', {});
  },
  upgradePlan(props: MonthlyUpgradeProps) {
    analytics.track('Monthly Upgrade Webview > Upgrade Plan', getMonthlyUpgradeEventProperties(props));
  },
  confirmationModalContinue(props: MonthlyUpgradeProps) {
    analytics.track('Monthly Upgrade Webview > Upgrade Plan > Continue', getMonthlyUpgradeEventProperties(props));
  },
  confirmationModalGoBack(props: MonthlyUpgradeProps) {
    analytics.track('Monthly Upgrade Webview > Upgrade Plan > Back', getMonthlyUpgradeEventProperties(props));
  },
  successfullyUpgraded(props: MonthlyUpgradeProps) {
    analytics.track(
      'Monthly Upgrade Webview > Upgrade Plan > Continue > Success',
      getMonthlyUpgradeEventProperties(props),
    );
  },
};

interface TroubleshootProps {
  petId?: string;
  troubleshootingReason?: gqlTypes.zendeskCancellationReason;
  replacementReason?: string;
  otherReasonText?: string;
  oldSku?: string;
  newSku?: string;
}

function getTroubleshootEventProperties(props: TroubleshootProps) {
  return {
    petId: props.petId,
    troubleshootingReason: props.troubleshootingReason,
    troubleshootingText: props.otherReasonText,
    replacementReason: props.replacementReason,
    oldSku: props.oldSku,
    newSku: props.newSku,
  };
}

// Returns an object containing Segment context fields.
function getTroubleshootEventContext() {
  // Params search string looks like "?platform=android&app_version=3.5.41-internal"
  const paramsString = window.location.search;
  const searchParams = new URLSearchParams(paramsString);
  return {
    os: { name: searchParams.get('platform') },
    app: { version: searchParams.get('app_version') },
  };
}

export const troubleshoot = {
  pageLoad(props: TroubleshootProps, pageName: string) {
    analytics.track(`Troubleshoot > Page Load: ${pageName}`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  troubleshootStartBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  selectedAndSubmittedTroubleshootingReason(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Selected and submitted reason`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  tellUsMoreBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Tell Us More > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  tellUsMoreClose(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Tell Us More > Close`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  tellUsMoreContinue(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Tell Us More > Continue`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  contactCxBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Contact CX > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  contactCxClose(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Contact CX > Close`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  chatWithCx(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Contact CX > Chat`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  callCx(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Contact CX > Call`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  submittedOtherTroubleshootingReason(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Other > Submitted reason`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  otherReasonBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Other > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  otherReasonClose(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Other > Close`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  troubleshootingTicketConfirmationDoneClicked(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Other > Done`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  submittedReplacementTicket(props: TroubleshootProps) {
    analytics.track(
      `Troubleshoot > Physically Damaged S3 > Submit Replacement Request`,
      getTroubleshootEventProperties(props),
      {
        context: getTroubleshootEventContext(),
      },
    );
  },
  replaceCollarBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Physically Damaged S3 > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  replaceCollarClose(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Physically Damaged S3 > Close`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  series2UpgradeClicked(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Physically Damaged S2 > Upgrade clicked`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  series2UpgradeBack(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Physically Damaged S2 > Back`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
  series2UpgradeClose(props: TroubleshootProps) {
    analytics.track(`Troubleshoot > Physically Damaged S2 > Close`, getTroubleshootEventProperties(props), {
      context: getTroubleshootEventContext(),
    });
  },
};

export const errorScreen = {
  pageLoad() {
    analytics.track('Page Load: Error Screen', {});
  },
  callCx() {
    analytics.track('Error Screen > Call', {});
  },
  back() {
    analytics.track('Error Screen > Back', {});
  },
  close() {
    analytics.track('Error Screen > Close', {});
  },
};

export const prepaidActivation = {
  submitCreditCard() {
    analytics.track('Prepaid activation > Submit credit card', {});
  },

  applePayBegin() {
    analytics.track('Prepaid activation > Apple pay begin', {});
  },

  applePaySuccess() {
    analytics.track('Prepaid activation > Apple pay success', {});
  },

  applePayError(message: string) {
    analytics.track('Prepaid activation > Apple pay error', { message });
  },

  applePayCancel() {
    analytics.track('Prepaid activation > Apple pay cancel', {});
  },

  nonSafariApplePayClick() {
    analytics.track('Prepaid activation > Non Safari Apple pay click', {});
  },

  nonSafariApplePayClose() {
    analytics.track('Prepaid activation > Non Safari Apple pay closed', {});
  },

  done() {
    analytics.track('Prepaid activation > Done', {});
  },
};

export const airvetManagement = {
  index: {
    pageLoad(pageName: string) {
      analytics.track(`Page Load: ${pageName}`, {});
    },
  },
  cancellation: {
    surveyBack() {
      analytics.track('Cancel Airvet Membership > Back', {});
    },
    surveyClose() {
      analytics.track('Cancel Airvet Membership > Close', {});
    },
    successClose() {
      analytics.track('Cancel Airvet Membership > Yes, cancel Airvet membership > Close', {});
    },
    cancelConfirmationModalContinue(selectedReason: string) {
      analytics.track(`Cancel Airvet Membership > ${selectedReason} > Yes, cancel Airvet membership`, {});
    },
    cancelConfirmationModalCancel() {
      analytics.track('Cancel Airvet Membership > Nevermind, go back', {});
    },
  },
};

export const renewAndSave = {
  viewed() {
    analytics.track('Renew And Save Webview Viewed', {});
  },
  earlyRenewPlan() {
    analytics.track('Renew And Save Webview > Renew Plan', {});
  },
  confirmationModalContinue() {
    analytics.track('Renew And Save Webview > Renew Plan > Continue', {});
  },
  confirmationModalGoBack() {
    analytics.track('Renew And Save Webview > Renew Plan > Back', {});
  },
  successfullyRenewedAndSaved() {
    analytics.track('Renew And Save Webview > Renew Plan > Continue > Success', {});
  },
};

export const productListingPage = {
  pageViewed() {
    analytics.track('PLP > Page Viewed', {});
  },

  miniShopNow() {
    analytics.track('PLP > Mini > Shop Now', {});
  },
  miniLearnMore() {
    analytics.track('PLP > Mini > Learn More', {});
  },
  miniDetailsExpand() {
    analytics.track('PLP > Mini > See Details Expand', {});
  },
  miniDetailsCollapse() {
    analytics.track('PLP > Mini > See Details Collapse', {});
  },

  series3PlusShopNow() {
    analytics.track('PLP > Series 3+ > Shop Now', {});
  },
  series3PlusLearnMore() {
    analytics.track('PLP > Series 3+ > Learn More', {});
  },
  series3PlusDetailsExpand() {
    analytics.track('PLP > Series 3+ > See Details Expand', {});
  },
  series3PlusDetailsCollapse() {
    analytics.track('PLP > Series 3+ > See Details Collapse', {});
  },

  ultraShopNow() {
    analytics.track('PLP > Ultra > Shop Now', {});
  },
  ultraLearnMore() {
    analytics.track('PLP > Ultra > Learn More', {});
  },
  ultraDetailsExpand() {
    analytics.track('PLP > Ultra > See Details Expand', {});
  },
  ultraDetailsCollapse() {
    analytics.track('PLP > Ultra > See Details Collapse', {});
  },

  miniCatsShopNow() {
    analytics.track('PLP > Mini Cats > Shop Now', {});
  },
  miniCatsDetailsExpand() {
    analytics.track('PLP > Mini Cats > See Details Expand', {});
  },
  miniCatsDetailsCollapse() {
    analytics.track('PLP > Mini Cats > See Details Collapse', {});
  },

  comparisonCompareProducts() {
    analytics.track('PLP > Comparison > Compare Products', {});
  },

  keepShoppingAccessories() {
    analytics.track('PLP > Keep Shopping > Accessories', {});
  },
  keepShoppingSupplements() {
    analytics.track('PLP > Keep Shopping > Supplements', {});
  },
  keepShoppingNano() {
    analytics.track('PLP > Keep Shopping > Nano', {});
  },

  freeTrialStartFreeTrial() {
    analytics.track('PLP > Free Trial > Start Free Trial', {});
  },

  scroll50() {
    analytics.track('PLP > Scroll > 50%', {});
  },
  scroll75() {
    analytics.track('PLP > Scroll > 75%', {});
  },
  scroll100() {
    analytics.track('PLP > Scroll > 100%', {});
  },
};

export const inAppShop = {
  opened() {
    analytics.track('In-App Shop > Opened', {});
  },
  checkoutOpened() {
    analytics.track('In-App Shop > Checkout Opened', {});
  },
  orderConfirmed() {
    analytics.track('In-App Shop > Order Confirmed', {});
  },
  orderCompleted() {
    analytics.track('In-App Shop > Order Completed', {});
  },
  orderFailed() {
    analytics.track('In-App Shop > Order Failed', {});
  },
  itemRemoved() {
    analytics.track('In-App Shop > Item Removed', {});
  },
  fullCheckoutTapped() {
    analytics.track('In-App Shop > Full Checkout Tapped', {});
  },
  accountEditOpened(field: 'shipping' | 'billing') {
    analytics.track('In-App Shop > Account Edit Opened', { field });
  },
  accountEditSaved(field: 'shipping' | 'billing') {
    analytics.track('In-App Shop > Account Edit Saved', { field });
  },
};

export const partnerLandingPage = {
  pageViewed(partnerSlug: string) {
    analytics.track('Partner Landing > Page Viewed', { partnerSlug });
  },
  series3PlusCtaClicked(partnerSlug: string) {
    analytics.track('Partner Landing > Series 3+ > CTA Clicked', { partnerSlug });
  },
  miniCtaClicked(partnerSlug: string) {
    analytics.track('Partner Landing > Mini > CTA Clicked', { partnerSlug });
  },
  miniForCatsCtaClicked(partnerSlug: string) {
    analytics.track('Partner Landing > Mini For Cats > CTA Clicked', { partnerSlug });
  },
  redirectedFromPlp(partnerSlug: string) {
    analytics.track('Partner Landing > Redirected From PLP', { partnerSlug });
  },
};

export const seriesThreePlusUpgrade = {
  viewed() {
    analytics.track('Series Three Plus Webview > Viewed', {});
  },
  confirmationModalOpened() {
    analytics.track('Series Three Plus Webview > Open Modal', {});
  },
  confirmationModalContinue() {
    analytics.track('Series Three Plus Webview > Open Modal > Continue', {});
  },
  confirmationModalGoBack() {
    analytics.track('Series Three Plus Webview > Open Modal > Back', {});
  },
  successfullyUpgradedToSeriesThreePlus() {
    analytics.track('Series Three Plus > Open Modal > Continue > Success', {});
  },
};

export const joinPack = {
  viewed() {
    analytics.track('Join Pack Webview Viewed', {});
  },
  confirmationModalOpen() {
    analytics.track('Join Pack Webview > Join Pack', {});
  },
  confirmationModalContinue() {
    analytics.track('Join Pack Webview > Join Pack > Continue', {});
  },
  confirmationModalGoBack() {
    analytics.track('Join Pack Webview > Join Pack > Back', {});
  },
  successfullyJoinedPack() {
    analytics.track('Join Pack Webview > Join Pack > Continue > Success', {});
  },
};

interface WebSubscriptionManageProps {
  petId?: string;
  cancellationReason?: string;
  otherReasonText?: string;
  currentSku?: string;
  newSku?: string;
  confirmedCancelReason?: boolean;
  shownOffer?: boolean;
  techCancelReason?: string;
  issueDetailsText?: string;
  /** Chosen pause length, 1-3. */
  pauseMonths?: number;
  /** Plan length, so pause uptake can be segmented by 1 vs 3 vs 6 vs 12 month plans. */
  renewalMonths?: number;
  /** Pause state at the moment a resume was confirmed — the read on accidental-charge rate. */
  pauseStatusAtResume?: PauseStatus['kind'];
}

function getWebSubscriptionManageEventProperties(props: WebSubscriptionManageProps) {
  return {
    petId: props.petId,
    cancellationReason: props.cancellationReason,
    cancellationText: props.otherReasonText,
    oldSku: props.currentSku,
    newSku: props.newSku,
    confirmedCancelReason: props.confirmedCancelReason,
    shownOffer: props.shownOffer,
    techCancelReason: props.techCancelReason,
    issueDetailsText: props.issueDetailsText,
    pauseMonths: props.pauseMonths,
    renewalMonths: props.renewalMonths,
    pauseStatusAtResume: props.pauseStatusAtResume,
  };
}

export const webSubscriptionManage = {
  overview: {
    pageLoad() {
      analytics.track('Web Subscription Manage > Page Load: Overview', {});
    },
    edit(props: WebSubscriptionManageProps) {
      analytics.track('Web Subscription Manage > Overview > Edit', getWebSubscriptionManageEventProperties(props));
    },
    clickedResume(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Clicked resume membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    closedResume(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Closed resume membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedResume(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Confirmed resume membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    resumeSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Successfully resumed membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    clickedReactivate(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Clicked reactivate membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    closedReactivate(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Closed reactivate membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedReactivate(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Confirmed reactivate membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    reactivateSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Successfully reactivated membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    addSubscriptionPickedPlan(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Add Subscription > Chose plan',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    addSubscriptionConfirmed(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Add Subscription > Confirmed adding subscription',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    addSubscriptionCompleted(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Add Subscription > Successfully added subscription',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    addSubscriptionOpened(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Add Subscription > Opened',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    addSubscriptionClosed(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Add Subscription > Closed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    unassignedSubscriptionCancelClicked(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Unassigned Subscription > Cancel clicked',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    unassignedSubscriptionCancelClosed(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Unassigned Subscription > Cancel closed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    unassignedSubscriptionCancelConfirmed(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Unassigned Subscription > Cancel confirmed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    unassignedSubscriptionCancelSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Unassigned Subscription > Successfully canceled subscription',
        getWebSubscriptionManageEventProperties(props),
      );
    },
  },
  modify: {
    pageLoad() {
      analytics.track('Web Subscription Manage > Page Load: Modify', {});
    },
    updateMembership(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Update membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedUpdateMembership(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Confirmed update membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    closedUpdateMembership(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Closed update membership confirmation',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    updateMembershipSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Successfully updated membership',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    clickedCancel(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Clicked Cancel',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    closedCancelDrawer(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Closed Cancel drawer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedCancelReason(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Confirmed cancel reason',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    claimedRetentionOffer(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Claimed retention offer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    retentionDiscountSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Successfully redeemed retention discount offer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    retentionFreeMonthSuccess(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Successfully redeemed free month offer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    canceled(props: WebSubscriptionManageProps) {
      analytics.track('Web Subscription Manage > Modify > Canceled', getWebSubscriptionManageEventProperties(props));
    },
    cancelCompleted(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Cancel completed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    /**
     * The pause offer leading the cancel flow appeared. Fires on the transition into the page rather
     * than from its render, so it counts entries and not re-renders — and it is the denominator the
     * offer's accept/decline rates are measured against, since that page has no click to enter.
     */
    shownPauseOffer(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Shown pause offer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    /** The member saw the pause offer and continued to cancel anyway. */
    declinedPauseOffer(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Declined pause offer',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedPause(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Confirmed pause',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    pauseCompleted(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Pause completed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    /**
     * Fires on every change, not just the final choice — otherwise "picked 3" is indistinguishable
     * from "picked 1 then 3" and default-option bias is invisible.
     */
    pauseDurationSelected(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Pause duration selected',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    /** `confirmedPause` fires before the mutation resolves, so failures need their own event. */
    pauseFailed(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Pause failed',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    createdCXSupportTicket(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Create CX Support Ticket',
        getWebSubscriptionManageEventProperties(props),
      );
    },
    confirmedTechCancelReason(props: WebSubscriptionManageProps) {
      analytics.track(
        'Web Subscription Manage > Modify > Confirmed tech issue',
        getWebSubscriptionManageEventProperties(props),
      );
    },
  },
};

export const quizQuestionPage = {
  quizButtonTapped() {
    const isMobile = isMobileDevice();
    analytics.track('PLP > Quiz Button Tapped', { deviceType: isMobile ? 'mobile' : 'desktop' });
  },
  quizPetTypeSelected(petType: string) {
    analytics.track('Quiz > Pet Type', { petType });
  },
  quizPetNamePageEntered() {
    analytics.track('Quiz > Pet Name', {});
  },
  quizPetNameNext(petName: string) {
    analytics.track('Quiz > Pet Name > Next', { petName });
  },
  quizPetBreedPageEntered() {
    analytics.track('Quiz > Pet Breed', {});
  },
  quizPetBreedNext(petBreed: string) {
    analytics.track('Quiz > Pet Breed > Next', { petBreed });
  },
  quizPetAgePageEntered() {
    analytics.track('Quiz > Pet Age', {});
  },
  quizPetAgeNext(petAge: string) {
    analytics.track('Quiz > Pet Age > Next', { petAge });
  },
  quizPetSizePageEntered() {
    analytics.track('Quiz > Pet Size', {});
  },
  quizPetSizeNext(petSize: string) {
    analytics.track('Quiz > Pet Size > Next', { petSize });
  },
  quizPetLocationPageEntered() {
    analytics.track('Quiz > Pet Location', {});
  },
  quizPetLocationNext(petLocation: string) {
    analytics.track('Quiz > Pet Location > Next', { petLocation });
  },
  quizPetTellUsMorePageEntered() {
    analytics.track('Quiz > Tell us more', {});
  },
  quizTellUsMoreNext(tellUsMore: string) {
    analytics.track('Quiz > Tell us more > Next', { tellUsMore });
  },
  quizWhatsImportantToYouPageEntered() {
    analytics.track('Quiz > Whats important to you', {});
  },
  quizWhatsImportantToYouNext(importantToYou: string) {
    analytics.track('Quiz > Whats important to you > Next', { importantToYou });
  },
  quizEnterEmailPageEntered() {
    analytics.track('Quiz > Enter your email', {});
  },
  quizEnterEmailNext(email: string) {
    analytics.track('Quiz > Enter your email > Next', { email });
  },
  quizEnterEmailSkipped() {
    analytics.track('Quiz > Enter your email > Skip', {});
  },
  // has_email says whether a lead was captured; the address itself never rides on the event
  quizCompleted(props: { recommendation: string; source: string; version: number; hasEmail: boolean }) {
    analytics.track('Quiz > Completed', {
      recommendation: props.recommendation,
      source: props.source,
      version: props.version,
      has_email: props.hasEmail,
    });
  },
  quizSuggestedDevice(deviceType: string) {
    analytics.track('Quiz > Suggested Device', { deviceType });
  },
  quizSuggestedDeviceShop() {
    analytics.track('Quiz > Suggested Device > Shop', {});
  },
  quizFreeTrialButtonTapped() {
    analytics.track('Quiz > Suggested Device > Free Trial', {});
  },

  // Cat Specific
  quizPetTimeSpentPageEntered() {
    analytics.track('Quiz > Time Spent (Cat)', {});
  },
  quizPetTimeSpentNext(spendTime: string) {
    analytics.track('Quiz > Time Spent (Cat) > Next', { spendTime });
  },
};

export function quizPdpBannerShown() {
  analytics.track('Quiz > PDP Banner Shown', {});
}

export function quizPdpBannerDismissed() {
  analytics.track('Quiz > PDP Banner Dismissed', {});
}

export const crossShopSection = {
  productTapped(productId: CollarProductId) {
    analytics.track('Cross Shop > Product Tapped', { productId });
  },
  compareAllProductsTapped() {
    analytics.track('Cross Shop > Compare All Products Tapped', {});
  },
  takeTheQuizTapped() {
    analytics.track('Cross Shop > Take The Quiz Tapped', {});
  },
};
